/* verqo — Nav Preview Hub
   Master index page for reviewers. Lists every route we've built.
*/
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SELLER_NAV } from "../../../config/nav.seller";
import { BUYER_NAV } from "../../../config/nav.buyer";
import { VerqoLockup } from "../../../components/VerqoLockup";

function Card({ href, label, desc, badge }) {
  return (
    <Link
      to={href}
      data-testid={`hub-link-${href}`}
      className="group block rounded-xl border border-slate-200 bg-white p-4 hover:border-lime-400 hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-2">
            {label}
            {badge && (
              <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-lime-100 text-lime-800">
                {badge}
              </span>
            )}
          </div>
          {desc && <div className="mt-1 text-[11.5px] text-slate-500 leading-snug">{desc}</div>}
          <div className="mt-2 text-[10.5px] font-mono text-slate-400 truncate">{href}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-lime-700 group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>
    </Link>
  );
}

function Section({ title, cards }) {
  return (
    <section className="mb-14" data-testid={`hub-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <h2 className="text-[13px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-4">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards}
      </div>
    </section>
  );
}

export default function NavPreviewHub() {
  const sellerPages = SELLER_NAV.filter(t => t.mega).flatMap(t => t.columns.flatMap(c => c.items));
  const buyerPages  = BUYER_NAV.filter(t => t.mega).flatMap(t => t.columns.flatMap(c => c.items));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-24">
      <header className="sticky top-0 inset-x-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <VerqoLockup />
            <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              Nav preview · not live
            </span>
          </div>
          <div className="flex items-center gap-2 text-[12px]">
            <Link to="/preview/nav/seller"  className="px-3 py-1.5 rounded-md bg-slate-900 text-white font-semibold" data-testid="hub-seller-landing">Seller landing</Link>
            <Link to="/preview/nav/buyers"  className="px-3 py-1.5 rounded-md bg-lime-300 text-slate-900 font-semibold" data-testid="hub-buyer-landing">Buyer landing</Link>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 pt-14">
        <div className="mb-14">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">Preview build</div>
          <h1 className="mt-4 text-[36px] md:text-[52px] leading-[1.05] tracking-tight font-semibold text-slate-900">
            The new marketing nav — <span className="italic text-slate-400">every page, one click away.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] text-slate-600 leading-relaxed">
            This hub lists every new route in the mega-menu build. Click into any page to review the design and copy — nothing here is live yet. When you&apos;re happy, I&apos;ll wire this into <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">/</code> and <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">/buyers</code>.
          </p>
        </div>

        <Section
          title="Landings & pricing"
          cards={[
            <Card key="1" href="/preview/nav/seller-trimmed" label="Seller landing (TRIMMED)" desc="Real homepage with 3 sections moved to sub-pages (How-it-works, Compare, Coming-soon). Buyer sections + pricing kept at full size." badge="Trimmed" />,
            <Card key="2" href="/preview/nav/buyers-trimmed" label="Buyer landing (TRIMMED)"  desc="Real /buyers page with Roadmap section moved to /company/how-it-works. Everything else kept at full size." badge="Trimmed" />,
            <Card key="3" href="/preview/nav/seller"          label="Seller landing (nav demo)" desc="Design showcase — shows the mega-menu with quick access to every route." badge="Demo" />,
            <Card key="4" href="/preview/nav/buyers"          label="Buyer landing (nav demo)"  desc="Design showcase — buyer-side mega-menu with quick access to every route." badge="Demo" />,
            <Card key="5" href="/preview/nav/pricing"         label="Seller pricing"    desc="4-tier SaaS pricing page." badge="Pricing" />,
            <Card key="6" href="/preview/nav/buyers/pricing"  label="Buyer pricing"     desc="Free · PAYG financing · Enterprise." badge="Pricing" />,
          ]}
        />

        <Section
          title="Seller · Product (12 pages)"
          cards={sellerPages.filter(p => p.href.includes("/product/")).map(p => (
            <Card key={p.href} href={p.href} label={p.label} desc={p.desc} />
          ))}
        />

        <Section
          title="Seller · Solutions (9 pages)"
          cards={SELLER_NAV.find(t => t.id === "solutions").columns.flatMap(c => c.items).map(p => (
            <Card key={p.href} href={p.href} label={p.label} desc={p.desc} />
          ))}
        />

        <Section
          title="Buyer · Product (13 pages)"
          cards={buyerPages.filter(p => p.href.includes("/product/")).map(p => (
            <Card key={p.href} href={p.href} label={p.label} desc={p.desc} />
          ))}
        />

        <Section
          title="Buyer · Solutions (10 pages)"
          cards={BUYER_NAV.find(t => t.id === "solutions").columns.flatMap(c => c.items).map(p => (
            <Card key={p.href} href={p.href} label={p.label} desc={p.desc} />
          ))}
        />

        <Section
          title="Company (11 pages · shared)"
          cards={SELLER_NAV.find(t => t.id === "company").columns.flatMap(c => c.items).map(p => (
            <Card key={p.href} href={p.href} label={p.label} desc={p.desc} />
          ))}
        />
      </div>
    </div>
  );
}
